import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';
import { getFormValues, change as changeFieldValue, untouch } from 'redux-form';
import { get, isEmpty, isNull, isNumber, throttle } from 'lodash';
import { injectIntl } from 'react-intl';

import { CREATE_APPOINTMENT_FORM } from 'modules/appointmentsManagement/constants/formTypes';
import { getUserGenderOptions } from 'modules/appointmentsManagement/selectors/appointments';
import { parsePhoneNumber, removePlus } from 'modules/user/utils/formatPhoneNumber';
import checkIsMobile from 'shared/utils/checkMobileDevice';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { getSelectedContract } from 'modules/appointmentsManagement/selectors/contracts';
import { getPatientByExternalId } from 'modules/appointmentsManagement/actions/_api/getPatientByExternalId';
import { getCommonUserConfig } from 'modules/user/selectors/users';

import PatientDetailsForm from './PatientDetailsForm';

const mapStateToProps = (state, ownProps) => {
  const { patientIndex, type, intl } = ownProps;
  const formValues = getFormValues(CREATE_APPOINTMENT_FORM)(state) || {};

  const isCustomer = type === 'customer';
  const customerIsPatient = !isNull(formValues.customerIsPatientIndex) && formValues.customerIsPatientIndex === patientIndex;
  const disableCustomerIsPatient = !isNull(formValues.customerIsPatientIndex) && formValues.customerIsPatientIndex !== patientIndex;

  const customerCheckStatus = get(formValues, 'customer.checkStatus', requestsStatuses.default);
  const patientCheckStatus = get(formValues, `patient[${ patientIndex }].checkStatus`, requestsStatuses.default);
  const patientPermissions = get(formValues, `patient[${ patientIndex }].initUser.permissions`);
  const customerPermissions = get(formValues, 'customer.initUser.permissions');

  const getContract = value => getSelectedContract(state, value);

  return {
    form: CREATE_APPOINTMENT_FORM,
    formValues,
    getContract,
    patientIndex,
    isCustomer,
    customerIsPatient,
    disableCustomerIsPatient,
    userCheckStatus: isCustomer ? customerCheckStatus : patientCheckStatus,
    userPermissions: isCustomer || customerIsPatient ? customerPermissions : patientPermissions,
    userGenderOptions: getUserGenderOptions(intl),
    userFieldConfig: getCommonUserConfig(state),
    isMobile: checkIsMobile(),
  };
};

const mapDispatchToProps = (dispatch) => ({
  checkUserByPolicyId: throttle((data) => dispatch(getPatientByExternalId(data)), 0, { leading: true }),
  changeFieldValue: (...props) => dispatch(changeFieldValue(...props)),
  untouch,
});

const defaultUsersModel = { externalId: '', userId: null, firstName: '', lastName: '', email: null, dateOfBirth: '', gender: 'UNKNOWN', phoneNumber: '', initUser: null };

const clearCustomerFields = (form, value, field, handler, defaultCountryCode = '') => {
  handler(form, field, {
    ...defaultUsersModel,
    externalId: value,
    phoneNumber: { countryCode: removePlus(defaultCountryCode), phone: '' },
    checkStatus: requestsStatuses.default,
  });
};

const setCustomerFields = (form, field, user, handler, lastSearchValue) => {
  const data = Object.entries(defaultUsersModel).reduce((acc, [key, value]) => {
    let userFormValue = user[key];
    if (key === 'dateOfBirth' && userFormValue) {
      userFormValue = `${ new Date(+userFormValue).getFullYear() }`;
    }

    if (key === 'phoneNumber' && userFormValue) {
      userFormValue = parsePhoneNumber(userFormValue);
    }

    return { ...acc, [key]: userFormValue || value };
  }, {});

  handler(form, field, {
    ...data,
    initUser: user,
    checkStatus: requestsStatuses.success,
    lastSearchValue,
  });
};

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
  withHandlers({
    setCustomerIsPatient: ({
      formValues: { patient = [] },
      patientIndex,
      changeFieldValue,
      form,
      userPhoneCountryCode,
    }) => index => {
      const result = [...patient];

      if (isNumber(index) && typeof patient[patientIndex] === 'object') {
        result[patientIndex] = { initUser: 'customer' };
      } else {
        result[patientIndex] = { phoneNumber: { countryCode: removePlus(userPhoneCountryCode) } };
      }
      changeFieldValue(form, 'patient', result);
      changeFieldValue(form, 'customerIsPatientIndex', index);
    },
  }),
  withHandlers({
    handleUserIdChange: ({
      checkUserByPolicyId,
      form,
      formValues,
      getContract,
      changeFieldValue,
      userPhoneCountryCode,
      patientIndex,
      setCustomerIsPatient,
    }) => (userType, inputValue) => {
      const value = inputValue ? inputValue.trim() : inputValue;
      const field = userType === 'customer' ? 'customer' : `patient[${ patientIndex }]`;
      const lastSearchValue = get(formValues, `${ field }.lastSearchValue`);
      const checkStatus = get(formValues, `${ field }.checkStatus`);

      if (lastSearchValue === value || (!value && !lastSearchValue)) return;

      const customerExternalId = get(formValues, 'customer.externalId');
      const customerIsAlreadyAPatient = !isEmpty(get(formValues, 'patient', []).find(patient => patient.initUser === 'customer'));

      if (userType === 'patient' && customerExternalId === value && !customerIsAlreadyAPatient) {
        return setCustomerIsPatient(patientIndex);
      }

      if (!value && checkStatus === requestsStatuses.success) {
        return clearCustomerFields(form, '', field, changeFieldValue, userPhoneCountryCode);
      }

      const currentContract = getContract(formValues.contractId);

      changeFieldValue(form, `${ field }.checkStatus`, requestsStatuses.pending);
      checkUserByPolicyId(value, currentContract.customerRootOrgNodeId)
        .then(({ payload: user }) => {
          if (userType === 'patient' && customerExternalId === user.externalId && !customerIsAlreadyAPatient) {
            setCustomerIsPatient(patientIndex);
          } else {
            setCustomerFields(form, field, user, changeFieldValue, value);
          }
        })
        .catch(() => {
          if (checkStatus === requestsStatuses.success) {
            clearCustomerFields(form, value, field, changeFieldValue, userPhoneCountryCode);
          } else {
            changeFieldValue(form, `${ field }.checkStatus`, requestsStatuses.default);
          }
          changeFieldValue(form, 'organizationId', currentContract.customerOrgNodeId);
        });
    },
    patientIdPreValidation: ({ formValues, intl, patientIndex }) => (value, a, b, field) => {
      if (!value || field.includes('customer')) return undefined;

      const customerId = get(formValues, 'customer.externalId');
      const alreadyInGroup = get(formValues, 'patient', [])
        .filter((patient, index) =>
          value && (patient.externalId === value
            || (patient.initUser === 'customer' && value === customerId && index !== patientIndex)));

      return alreadyInGroup.length > 1
        ? intl.formatMessage({
          id: 'AdminConsole.Appointments.CreateForm.PatientDuplicationError.Text',
          defaultMessage: '** Patient already exists in the group',
        })
        : undefined;
    },
  }),
)(PatientDetailsForm);
